import { isPlatformServer } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  PLATFORM_ID,
  ViewChild,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ArchiveSize, GalleryAccessLevel, GalleryDto, GalleryStatus } from '@picflow/types';
import { firstValueFrom } from 'rxjs';
import { ScreenSizes } from '../../../classes/screen-sizes.class';
import { GalleryStatusContextComponent } from '../../../components/gallery-status-context/gallery-status-context.component';
import { maxZipRequestSize } from '../../../consts';
import { GalleryTreeComponent } from '../../../gallery-shared/components/gallery-tree/gallery-tree.component';
import { DownloadAllDialogComponent } from '../../../gallery-shared/dialogs/download-all-dialog/download-all-dialog.component';
import { Filter } from '../../../gallery-shared/interfaces/filter.interface';
import { GROWTHBOOK } from '../../../growthbook';
import { getAllowedAndEnabledFeedbackOptions, getPermissions } from '../../../lib/get-allowed-feedback-options';
import { getFullPath } from '../../../lib/get-full-path';
import { swapGalleryInUrl } from '../../../lib/swap-gallery-in-url';
import { GalleryStatusColorPipe } from '../../../pipes/gallery-status-color.pipe';
import { GalleryStatusIconPipe } from '../../../pipes/gallery-status-icon.pipe';
import { ArchiveService } from '../../../services/archive.service';
import { DialogService } from '../../../services/dialog.service';
import { GalleryService } from '../../../services/gallery.service';
import { StateService } from '../../../services/state.service';
import { GalleryDownloadMenuOptionsComponent } from '../../../shared-extra/components/gallery-download-menu-options/gallery-download-menu-options.component';
import { AvatarPipe } from '../../../shared-extra/pipes/avatar.pipe';
import { UserGuestNamePipe } from '../../../shared-extra/pipes/user-guest-name.pipe';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { GuestProfileDialogComponent } from '../../dialogs/guest-profile-dialog/guest-profile-dialog.component';
import { ViewFilterComponent } from '../view-filter/view-filter.component';
import { ViewProfileMenuComponent } from '../view-profile-menu/view-profile-menu.component';

@Component({
  selector: 'app-view-gallery-header',
  standalone: true,
  templateUrl: './view-gallery-header.component.html',
  styleUrls: ['./view-gallery-header.component.scss'],
  imports: [
    HeaderComponent,
    ButtonDirective,
    SvgComponent,
    ContextMenuComponent,
    RouterLink,
    GalleryTreeComponent,
    ViewProfileMenuComponent,
    AvatarPipe,
    TranslatePipe,
    GalleryDownloadMenuOptionsComponent,
    ViewFilterComponent,
    TooltipDirective,
    UserGuestNamePipe,
    GalleryStatusContextComponent,
    GalleryStatusIconPipe,
    GalleryStatusColorPipe,
    AvatarComponent,
  ],
})
export class ViewGalleryHeaderComponent implements OnChanges {
  @Input({ required: true }) gallery: GalleryDto;
  @Input({ required: true }) accessLevel: GalleryAccessLevel;
  @Input({ required: true }) mode: 'layout' | 'advanced';
  @Output() onFilterChange = new EventEmitter<Filter[]>();
  @ViewChild('viewFilter') viewFilter: ViewFilterComponent;

  state = inject(StateService);
  dialog = inject(DialogService);
  galleryService = inject(GalleryService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  archiveService = inject(ArchiveService);
  platformId = inject(PLATFORM_ID);
  private growthBook = inject(GROWTHBOOK);

  parentGallery: GalleryDto;
  isMobile: boolean;
  showGalleriesButton: boolean;
  parentLink?: string[];
  allowAdvancedMode: boolean;

  downloadingAll = false;
  preppingDownloadAll = false;
  filters: Filter[] = [];
  enabledAndAllowedFeedbackOptions: string[];

  @HostListener('window:resize', ['$event']) async winResize() {
    this.isMobile = ScreenSizes.screenSize() === 'mobile';
  }

  constructor() {
    if (isPlatformServer(this.platformId)) return;
    this.isMobile = ScreenSizes.screenSize() === 'mobile';
  }

  ngOnChanges(): void {
    if (isPlatformServer(this.platformId)) return;
    this.calcShowGalleriesButton();
    const permissions = getPermissions(this.gallery);

    this.allowAdvancedMode =
      permissions.gallery.clientAdvancedMode &&
      this.gallery.client_advanced_mode_enabled &&
      this.accessLevel !== 'view_only';

    this.enabledAndAllowedFeedbackOptions = getAllowedAndEnabledFeedbackOptions(
      this.gallery,
      this.state.plan,
      this.accessLevel
    );
  }

  async calcShowGalleriesButton() {
    if (!this.gallery) return;
    this.showGalleriesButton = false;
    this.parentLink = undefined;

    if (this.gallery['parent_gallery_id']) {
      this.showGalleriesButton = true;
      this.parentGallery = await this.galleryService.getGallery(this.gallery['parent_gallery_id']);
      this.parentLink = swapGalleryInUrl(this.gallery, this.parentGallery, getFullPath(this.route.snapshot));
    } else {
      const subGalleries = await this.galleryService.getSubGalleries(this.gallery.id);
      this.showGalleriesButton = subGalleries.length > 0;
    }
  }

  async openGuestProfileDialog() {
    await this.dialog.open(GuestProfileDialogComponent, { gallery: this.gallery, accessLevel: this.accessLevel });
  }

  async download(size: ArchiveSize) {
    if (this.downloadingAll) return;
    if (this.preppingDownloadAll) return;
    this.downloadingAll = true;
    const subGalleries = await this.galleryService.getSubGalleries(this.gallery.id);
    const requests = await firstValueFrom(
      this.archiveService.createGalleryArchives([this.gallery, ...subGalleries], size)
    );

    const totalDownloadSize = requests.reduce((acc, request) => acc + request.totalSize, 0n);

    if (totalDownloadSize > maxZipRequestSize) {
      const resp = await this.dialog.open(DownloadAllDialogComponent, {
        totalSize: totalDownloadSize,
        amAllowed: this.gallery.client_advanced_mode_enabled,
        galleries: [this.gallery, ...subGalleries],
      });
      this.downloadingAll = false;
      if (!resp) return;
      if (resp === 'am') return this.router.navigate(['./advanced'], { relativeTo: this.route });
      this.downloadingAll = true;
    }

    await Promise.all(
      requests.map(async (request, index) => {
        const archive = await firstValueFrom(
          this.archiveService.requestArchiveCreation(
            request.request,
            this.gallery.id,
            request.assetCount,
            request.totalSize,
            requests.length === 1 ? undefined : { index, total: requests.length }
          )
        );
        await firstValueFrom(this.archiveService.onArchiveDone(archive));
      })
    );

    this.downloadingAll = false;
  }

  async onFilter(filters: Filter[]) {
    this.filters = filters;
    this.onFilterChange.emit(filters);
  }

  updateGalleryStatus(status: GalleryStatus) {
    this.galleryService.updateGallery(this.gallery.id, { status });
    this.gallery.status = status;
  }
}
